import {compact} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import {DistinctChain, DistinctDomainKeyWithChainIndex} from 'antibodies/AntibodyTypedefs';
import {domainColor, chainColor} from 'antibodies/AntibodyUtils';
import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {GlobalSearchResponse} from 'search/GlobalSearchDAO';
import {
  Filter,
  FilterKey,
  GLOBAL_SEARCH_URL,
  keyToEmptyFilter,
  Operator,
  SortKey,
} from 'search/SearchConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import BenchChip from 'ui-library/chips/BenchChip';
import ChipSearchInput, {searchPost} from 'ui-library/inputs/ChipSearchInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';

export interface AntibodyDomainSearchInputProps extends BaseAntibodyComponentSearchInputProps {
  distinctDomain: DistinctDomainKeyWithChainIndex;
}

export interface AntibodyChainSearchInputProps extends BaseAntibodyComponentSearchInputProps {
  distinctChain: DistinctChain;
  chainIndex: number;
}

interface AntibodyComponentSearchInputProps extends BaseAntibodyComponentSearchInputProps {
  label: JSX.Element;
  labelText: string;
  swatchColor: string;
  swatchType: 'OUTLINE' | 'FILL';
  isRequired: boolean;
  placeholderOverride?: string;
}

interface BaseAntibodyComponentSearchInputProps {
  disabled: boolean;
  selectedProtein: string | null;
  setSelectedProtein: (newSelectedProtein: string | null) => void;
  linkedSchemaId: string | null;
  domainTypeFieldId?: string | null;
  domainTypeOptionId?: string | null;
  showSwatch?: boolean;
  registryId?: string;
}

/**
 * A search input and label for an antibody domain.
 */
export function AntibodyDomainSearchInput({
  distinctDomain,
  ...props
}: AntibodyDomainSearchInputProps): JSX.Element {
  const label = useMemo(
    () => (
      <>
        <Label>{distinctDomain.label}</Label>
      </>
    ),
    [distinctDomain.label]
  );

  return (
    <AntibodyComponentSearchInput
      {...props}
      label={label}
      labelText={distinctDomain.label}
      swatchColor={domainColor({distinctDomain}).baseColor}
      swatchType="FILL"
      isRequired={!distinctDomain.optional}
    />
  );
}

/**
 * A search input and label for an antibody chain.
 */
export function AntibodyChainSearchInput({
  distinctChain,
  chainIndex,
  ...sharedProps
}: AntibodyChainSearchInputProps): JSX.Element {
  const label = useMemo(() => <Label>{distinctChain.label}</Label>, [distinctChain.label]);

  return (
    <AntibodyComponentSearchInput
      {...sharedProps}
      label={label}
      labelText={distinctChain.label}
      swatchColor={chainColor(chainIndex, distinctChain.type)}
      swatchType="OUTLINE"
      isRequired={true}
      placeholderOverride={sharedProps.disabled ? 'Concatenated from domains...' : undefined}
    />
  );
}

/**
 * A wrapper around ChipSearchInput that can be used to associate an AA Sequence entity with a component of an antibody.
 * @param disabled Whether the ChipSearchInput should be disabled
 * @param label The label for the search input
 * @param labelText The text for the label
 * @param swatchColor The color of the swatch
 * @param swatchType The type of swatch
 * @param isRequired Whether the domain is required (not optional)
 * @param selectedProtein The API ID of the selected AA Sequence, if any
 * @param setSelectedProtein A function to update the selected protein
 * @param linkedSchemaId The API ID of the schema that the domain must be. null if any AA sequence is allowed
 * @param showSwatch Whether to show the swatch stripe. Defaults to true.
 * @param placeholderOverride Optional alternative placeholder text for the search input.
 */
function AntibodyComponentSearchInput({
  disabled,
  label,
  labelText,
  swatchColor,
  swatchType,
  isRequired,
  selectedProtein,
  setSelectedProtein,
  linkedSchemaId,
  domainTypeFieldId,
  domainTypeOptionId,
  registryId,
  showSwatch = true,
  placeholderOverride,
}: AntibodyComponentSearchInputProps): JSX.Element {
  const [query, setQuery] = useState('');

  return (
    <div
      className={css(styles.wrapper)}
      aria-label={labelText}
      {...getTestProps({component: 'AntibodyComponentSearchInput', element: 'root', key: labelText})}
      data-amp-safe={false}
    >
      {showSwatch && (
        <div
          className={css(styles.swatch)}
          style={{
            backgroundColor: swatchType === 'FILL' ? swatchColor : 'transparent',
            border: swatchType === 'OUTLINE' ? `1px solid ${swatchColor}` : undefined,
          }}
        />
      )}
      <FormGroup className={css(styles.searchWrapper)}>
        <FieldLabel label={label} isRequired={isRequired} />
        <ChipSearchInput
          autoFocus={false}
          chipClass={BenchChip}
          disabled={disabled}
          doSearch={searchPost}
          fetchParams={{
            offset: 0,
            limit: 10,
            sorts: [{key: SortKey.SORT_RELEVANCE_KEY, reverse: false}],
            filters: [
              {
                ...keyToEmptyFilter(FilterKey.TYPES),
                value: [ModelTypeConstants.PROTEIN],
              },
              // TODO: Do we instead want special visual treatment for archived schemas?
              {
                ...keyToEmptyFilter(FilterKey.ARCHIVE_PURPOSES),
                value: [NOT_ARCHIVED_PURPOSE_STRING],
              },
              // TODO: Do we instead want special visual treatment for empty proteins?
              {
                ...keyToEmptyFilter(FilterKey.PROTEIN_AMINO_ACIDS),
                operator: Operator.IS_NOT_EMPTY,
              },
              ...(registryId != null
                ? [{key: FilterKey.REGISTRY, operator: Operator.IS, value: registryId} satisfies Filter]
                : []),
              ...(linkedSchemaId != null
                ? [{...keyToEmptyFilter(FilterKey.SCHEMA), value: [linkedSchemaId]}]
                : []),
              ...(domainTypeFieldId != null && domainTypeOptionId != null
                ? [
                    {
                      ...keyToEmptyFilter(FilterKey.SCHEMA_FIELD),
                      fieldId: domainTypeFieldId,
                      fieldType: FieldType.SELECTOR,
                      operator: Operator.HAS_ONE_OF,
                      value: [domainTypeOptionId],
                    } satisfies Filter,
                  ]
                : []),
            ],
          }}
          getChipProps={(id) => ({
            id,
          })}
          getOptionDatasFromResponse={(response: GlobalSearchResponse) => {
            const {ids, fileSummariesById} = response;
            return ids.map((id) => {
              const summary = fileSummariesById[id];
              return {
                name: summary.name,
                icon: summary.tagSchema?.iconId ?? 'protein',
                value: id,
              };
            });
          }}
          isMulti={false}
          query={query}
          onQueryChange={(e) => {
            setQuery(e.target.value);
          }}
          onRemove={() => {
            setSelectedProtein(null);
          }}
          onSelect={(value: string) => {
            setSelectedProtein(value);
            setQuery('');
          }}
          placeholder={placeholderOverride ?? 'Search for AA sequence'}
          searchOnEmptyQuery={false}
          searchURL={GLOBAL_SEARCH_URL}
          value={compact([selectedProtein])}
        />
      </FormGroup>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {display: 'flex'},
  swatch: {
    width: '4px',
    minWidth: '4px',
    marginRight: '4px',
    flex: '0 0 4px',
    // These values are somewhat arbitrary. They are meant to make the swatch align with the top of the
    // label text and the bottom of the search box.
    marginTop: '3px',
    marginBottom: '15px',
    borderRadius: '1px',
  },
  searchWrapper: {width: '100%'},
});
